import { Layout, Avatar, Dropdown, Space, Skeleton, Grid, theme, Button } from 'antd';
import { UserOutlined, DownOutlined, MenuOutlined, IdcardOutlined } from '@ant-design/icons';
import { DisplaySettingsMenu } from './DisplaySettingsMenu';
import { PiiToggle } from './PiiToggle';
import NotificationBell from './NotificationBell';
import { useAuth } from '../auth/AuthContext';
import { useDisplaySettings } from '../theme/DisplaySettingsContext';
import logoLight from '../assets/Mimo_Logo_R.jpg';
import logoDark from '../assets/Mimo_Logo_Black_2020.jpg';
import { useNavigate } from 'react-router-dom';

const { Header } = Layout;
const { useBreakpoint } = Grid;
const { useToken } = theme;

type HeaderBarProps = {
  /**
   * Whether to show the hamburger menu button. Controlled by AppLayout based
   * on viewport breakpoint — only true on phone widths.
   */
  showHamburger?: boolean;
  /**
   * Callback fired when the hamburger button is clicked. AppLayout uses this
   * to open the mobile navigation drawer.
   */
  onHamburgerClick?: () => void;
};

const HeaderBar = ({ showHamburger = false, onHamburgerClick }: HeaderBarProps) => {
  const screens = useBreakpoint();
  const isMobile = screens.xs && !screens.sm;
  const auth = useAuth();
  const navigate = useNavigate();
  const { token } = useToken();
  const { resolvedTheme } = useDisplaySettings();

  const isDark = resolvedTheme === 'dark';
  const logoSrc = isDark ? logoDark : logoLight;

  // KAN-85: brand the header with the resolved cascading logo (vessel → org →
  // reseller, decided server-side). Falls back to the fixed MIMO logo when the
  // branding is null or carries no logo URL (resilient — never crashes).
  const branding = auth.branding && auth.branding.logoUrl ? auth.branding : null;

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === 'profile') {
      navigate('/profile');
    } else if (key === 'logout') {
      auth.logout();
    }
  };

  const userMenu = {
    items: [
      {
        key: 'profile',
        label: 'My Profile',
        icon: <IdcardOutlined />,
      },
      {
        type: 'divider' as const,
      },
      {
        key: 'logout',
        label: 'Logout',
        danger: true,
      },
    ],
    onClick: handleMenuClick,
  };

  return (
    <Header
      style={{
        background: token.colorBgContainer,
        padding: '0 12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: 12,
        boxShadow: `0 2px 8px ${token.colorSplit}`,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {showHamburger && (
          <Button
            type="text"
            icon={<MenuOutlined style={{ fontSize: 20 }} />}
            onClick={onHamburgerClick}
            aria-label="Open navigation menu"
            style={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        )}
        {branding ? (
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <img
              src={branding.logoUrl!}
              alt={branding.name}
              style={{
                height: isMobile ? 40 : 64,
                maxHeight: 72,
                objectFit: 'contain',
                display: 'block',
              }}
            />
            <span
              style={{
                fontSize: 12,
                color: token.colorTextSecondary,
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {branding.name}
            </span>
          </div>
        ) : (
          <img
            src={logoSrc}
            alt="MIMO Connect"
            style={{
              height: isMobile ? 40 : 64,
              maxHeight: 72,
              objectFit: 'contain',
              display: 'block',
            }}
          />
        )}
      </div>

      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 8 : 16,
        }}
      >
        {auth.authenticated && <NotificationBell />}
        <PiiToggle />
        <DisplaySettingsMenu />
        <Dropdown menu={userMenu} trigger={['click']}>
          <Space size="small" style={{ cursor: 'pointer' }}>
            {auth.authenticated && auth.user ? (
              <Avatar icon={<UserOutlined />} />
            ) : (
              <Skeleton.Avatar active size="default" />
            )}
            {!isMobile && auth.authenticated ? <span>{auth.user!.username || 'User'}</span> : null}
            <DownOutlined />
          </Space>
        </Dropdown>
      </div>
    </Header>
  );
};

export default HeaderBar;
